import React from 'react';
import { Tag, Row } from 'antd';
import { uploadFileToS3 } from '../../../shared/upload/uploadFileToS3';
import { getFileType } from '../../../../core/utils/constants/constants';
import { startCase } from 'lodash';
import { urgencyColors as configUrgencyColors } from '../configs/task.config';
import { space, fontSize as fontSizeTokens, fontWeight as fontWeightTokens, radius } from '../configs/designTokens';

// CP and CP DIRECT are distinct org levels that share the same features
export const CHANNEL_PARTNER_LEVELS = ['CHANNEL_PARTNER', 'CHANNEL_PARTNER_DIRECT'];
export const isChannelPartnerLevel = (level) => CHANNEL_PARTNER_LEVELS.includes(level);

// Levels that get the restricted, own-org-scoped task view (CP/CP-Direct + STANDARD),
export const RESTRICTED_TASK_LEVELS = [...CHANNEL_PARTNER_LEVELS, 'STANDARD'];
export const isRestrictedTaskLevel = (level) => RESTRICTED_TASK_LEVELS.includes(level);

export const getNumberWithSuffix = (number) => {
  // Input validation
  if (!Number.isInteger(number)) {
    throw new Error('Input must be an integer');
  }
  // Handle special cases for 11, 12, 13
  const specialCase = number % 100;
  if (specialCase >= 11 && specialCase <= 13) {
    return `${number}th `;
  }
  // Handle general cases
  switch (number % 10) {
    case 1:
      return `${number}st `;
    case 2:
      return `${number}nd `;
    case 3:
      return `${number}rd `;
    default:
      return `${number}th `;
  }
};

export const codeBlock = (text) => {
  const parts = [];
  const splitRegex = /(```[\s\S]*?```)/g;
  const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;

  // Split text into normal text and code blocks
  const textParts = text.split(splitRegex);

  textParts.forEach((part) => {
    if (part.startsWith('```')) {
      const match = codeBlockRegex.exec(part);
      if (match) {
        const [, lang, code] = match;
        parts.push({
          type: 'code',
          content: code.trim(),
          language: lang || 'plaintext',
        });
      }
      codeBlockRegex.lastIndex = 0;
    } else if (part) {
      parts.push({ type: 'text', content: part });
    }
  });

  return parts;
};

export const handleUploadFilesToS3 = async (files) => {
  try {
    const medias = [];
    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const media = await uploadFileToS3({ file });
        medias.push({
          ...media,
          fileType: getFileType(file.name),
        });
      }
    }
    return medias;
  } catch (error) {
    console.error('handleUploadFilesToS3 failed:', error);
    throw error;
  }
};

export const getLastCommentUser = (record) => {
  if (!record.attachedComments || record.attachedComments.length === 0) return null;
  const sortedComments = [...record.attachedComments].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  return sortedComments.length > 0 ? sortedComments[0].createdBy : null;
};

export const isUserAssignee = (user, record, includeThirdParty = false) => {
  if (!user || !record) return false;

  const isAssignee =
    (record.assigneeUser && record.assigneeUser.id === user.id) ||
    (record.assigneeUsers && record.assigneeUsers.some((assignee) => assignee.id === user.id));

  const isRequestor = record.createdBy && user.id === record.createdBy.id;

  if (includeThirdParty) {
    const lastCommentUser = getLastCommentUser(record);
    const isThirdPartyComment =
      lastCommentUser &&
      lastCommentUser.id !== (record.createdBy && record.createdBy.id) &&
      !isAssignee;

    return isThirdPartyComment || (isAssignee && !isRequestor);
  }

  return isAssignee && !isRequestor;
};

// A partner literally named "MediaJel Direct" is the direct umbrella — every advertiser under
// it is a direct deal. Kept in sync with the same const in forms/TaskBasicFields.js.
export const DIRECT_PARTNER_NAME = 'mediajel direct';

// Longest known username that prefixes `afterAt` (the text after '@'), ending at a non-word
// boundary so "@user." still matches "user". Returns the username or null. Shared by the comment
// renderer and the Note's MentionText so coloring matches the notify/inline-resolution logic.
export const matchKnownUsername = (afterAt, mentionUsers) =>
  (mentionUsers || [])
    .map((u) => u.username)
    .filter(
      (name) =>
        name &&
        afterAt.startsWith(name) &&
        !/[A-Za-z0-9_]/.test(afterAt.charAt(name.length))
    )
    .sort((a, b) => b.length - a.length)[0] || null;

// Map @username tokens in the given text(s) to user ids. Matches the backend boundary
// (`@user.`/`@user,` still resolve), longest-username first, blanking each match so a shorter
// prefix (e.g. "@randall" inside "@randall.tan") can't double-count. `texts` is a string or array.
const escapeRegExpMention = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export const getMentionedUserIds = (texts, mentionUsers) => {
  let remaining = (Array.isArray(texts) ? texts : [texts]).filter(Boolean).join('\n');
  if (!remaining) return [];
  const ids = [];
  [...(mentionUsers || [])]
    .filter((u) => u.username)
    .sort((a, b) => b.username.length - a.username.length)
    .forEach((u) => {
      const re = new RegExp(`@${escapeRegExpMention(u.username)}(?![A-Za-z0-9_])`, 'g');
      const replaced = remaining.replace(re, ' ');
      if (replaced !== remaining) {
        ids.push(u.id);
        remaining = replaced;
      }
    });
  return ids;
};

// On EDIT, ping only the mentions ADDED in this edit so people already mentioned before aren't
// re-pinged. Returns ids present in the new text(s) but not the old.
export const getAddedMentionedUserIds = (oldTexts, newTexts, mentionUsers) => {
  const before = new Set(getMentionedUserIds(oldTexts, mentionUsers));
  return getMentionedUserIds(newTexts, mentionUsers).filter((id) => !before.has(id));
};

// Direct-deal detection for READ surfaces (details / drawer / sheet). Mirrors the NAME-based
// rule the create/edit form uses (TaskBasicFields): a deal is Direct when the channel partner
// is "MediaJel Direct", or the advertiser shares the partner's name (the partner IS that
// advertiser). A form-created direct still HAS a channelPartner, so the old
// `advertiser && !channelPartner` heuristic never matched real directs.
export const isDirectTask = (task) => {
  if (!task) return false;
  const norm = (s) => (s || '').trim().toLowerCase();
  const cp = task.channelPartner;
  if (!cp) return false;
  const advName = task.advertiser && task.advertiser.name;
  // Mirrors the create/edit form (TaskBasicFields): Direct when the partner is a
  // CHANNEL_PARTNER_DIRECT-level org, is named "MediaJel Direct", or the advertiser shares the
  // partner's name (the partner IS that advertiser).
  return (
    cp.level === 'CHANNEL_PARTNER_DIRECT' ||
    norm(cp.name) === DIRECT_PARTNER_NAME ||
    (!!advName && norm(advName) === norm(cp.name))
  );
};

export const deduplicateUsers = (users) => {
  const userMap = new Map();
  users.forEach((user) => {
    const displayName = startCase(user.name || user.username);
    if (!userMap.has(displayName)) {
      userMap.set(displayName, user);
    }
  });
  return Array.from(userMap.values());
};

// Options for an assignee Select: the pickable pool plus the task's current assignee(s), so an
// already-assigned user who is no longer pickable (e.g. since disabled) still renders by name
// instead of a raw id. Deduped by id, not display name — two different people can share a name.
export const assigneeOptions = (pool, currentAssignees) => {
  const byId = new Map();
  [...(pool || []), ...(currentAssignees || [])].forEach((user) => {
    if (user && !byId.has(user.id)) byId.set(user.id, user);
  });
  return Array.from(byId.values());
};

export const copyTaskLink = (taskId, messageApi) => {
  const baseUrl = window.location.origin;
  const taskUrl = `${baseUrl}/tasks/details/${taskId}`;

  navigator.clipboard.writeText(taskUrl).then(
    () => {
      messageApi.success('Link copied to clipboard!');
    },
    () => {
      messageApi.error('Failed to copy link');
    }
  );
};

// special case for task status tags
const statusTagConfig = {
  NOT_STARTED: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Not Started' },
  TRIAGED: { bg: '#e6f7ff', color: '#1890ff', text: 'Triaged' },
  READY: { bg: '#e6fffb', color: '#13c2c2', text: 'Ready' },
  IN_PROGRESS: { bg: '#e6f7ff', color: '#1890ff', text: 'In Progress' },
  BLOCKED: { bg: '#fff1f0', color: '#ff4d4f', text: 'Blocked' },
  DEPENDENCY: { bg: '#fff0f6', color: '#eb2f96', text: 'Dependency' },
  ON_HOLD: { bg: '#fff7e6', color: '#faad14', text: 'On Hold' },
  COMPLETED: { bg: '#f6ffed', color: '#52c41a', text: 'Completed' },
  MONITORING: { bg: '#f9f0ff', color: '#722ed1', text: 'Monitoring' },
  DUPLICATE: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Duplicate' },
  INPUT_REQUIRED: { bg: '#fff7e6', color: '#fa8c16', text: 'Input Required' },
  NO_LONGER_NEEDED: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Not Needed' },
};

export const TaskStatusTag = ({ status }) => {
  const config = statusTagConfig[status] || { bg: '#f5f5f5', color: '#8c8c8c', text: 'Unknown' };

  return (
    <Tag
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: 0,
        borderRadius: radius.xl,
        padding: `2px ${space[3]}px`,
        fontSize: fontSizeTokens.caption,
        fontWeight: fontWeightTokens.medium,
      }}
    >
      <Row justify="space-around" type="flex" align="middle" style={{ gap: space[1.5] }}>
        <div
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: config.color,
          }}
        />
        {config.text}
      </Row>
    </Tag>
  );
};

export const urgencyColors = configUrgencyColors;

// Feature check utilities
export const hasUserCampaignsFeature = (currentUser) => {
  return currentUser?.features?.includes('CAMPAIGNS');
};

export const hasOrgCampaignsFeature = (currentUser) => {
  return currentUser?.role?.roleItems?.some(item => item.feature === 'CAMPAIGNS');
};

export const hasCampaignsFeature = (currentUser) => {
  return hasUserCampaignsFeature(currentUser) && hasOrgCampaignsFeature(currentUser);
};

// A comment needs text, an attachment, or a snippet. Shared so the re-open prompt gates on the
// same condition the submit handler validates with — otherwise the prompt fires on an empty box.
export const hasCommentContent = ({
  comment,
  attachedFiles,
  attachedImages,
  attachedCodeSnippet,
} = {}) =>
  !!(
    (comment && comment.trim()) ||
    (attachedFiles && attachedFiles.length) ||
    (attachedImages && attachedImages.length) ||
    (attachedCodeSnippet && attachedCodeSnippet.trim())
  );
